import * as React from 'react';
import { css, styled } from '../../styles';
import { Checkbox } from '../Checkbox/Checkbox';
import { IconButton } from '../IconButton';
import { mergeClassNames } from '../../utils/cssClassNameHelper';
import { Typography } from '../Typography/Typography';
import { Embolden } from '../Embolden';
import { anyChildrenChecked, findNode, isLeaf, isMatch } from './CheckBoxTreeUtils';
import { CheckBoxTreeProps, Node } from './types';

export interface NodeItemProps {
  node: Node;
  keepExpanded: CheckBoxTreeProps['keepExpanded'];
  checked: CheckBoxTreeProps['checked'];
  depth?: number;
  parentValue?: string;
  expand?: boolean;
  virtualized?: boolean;
  onExpand?: (event: React.MouseEvent) => void;
  onClick?: (event: React.MouseEvent) => void;
  onCheck?: (e: React.ChangeEvent) => void;
  searchTerm?: string;
  focusedNode?: string;
}

export const NodeItem: React.FC<React.PropsWithChildren<NodeItemProps>> = ({
  node,
  onExpand,
  onClick,
  onCheck,
  children,
  depth = 0,
  keepExpanded,
  checked,
  expand,
  virtualized,
  searchTerm,
  focusedNode,
  parentValue
}) => {
  const leaf = isLeaf(node);
  const isChecked = findNode(node, checked ?? []).match;

  const isPartiallyChecked = leaf && !isChecked && anyChildrenChecked(node.children || [], checked ?? []);
  const contentClassName = mergeClassNames('node-content', {
    'hidden-button': keepExpanded
  });

  const nodeClassName = mergeClassNames('node', {
    parent: !!node.children,
    leaf: !node.children,
    'no-match': node.match === false, // undefined, true are treated as match
    match: isMatch(node),
    'child-match': !!node.children?.find((child) => isMatch(child))
  });

  return (
    <div
      data-test={`node-${node.value}`}
      className={nodeClassName}
      id={node.value}
      data-focus={focusedNode === node.value ? 'true' : 'false'}
      data-parent-value={parentValue}
    >
      <div className={contentClassName}>
        {/** biome-ignore lint/a11y/noStaticElementInteractions: This div is used for indentation and has an onClick handler. */}
        <div style={{ width: 22 * depth, height: 38 }} onClick={onClick} />
        {!keepExpanded && (
          // biome-ignore lint/a11y/noStaticElementInteractions: This div is used for expanding/collapsing the node, and it has an onClick handler.
          <div
            className={!leaf ? 'container' : 'button-container'}
            data-test={expand ? 'collapse-icon' : 'expand-icon'}
            onClick={onClick}
          >
            {!leaf ? (
              <div className="node-expand-collapse hidden" style={{ display: 'inline-block', width: 23, height: 33 }} />
            ) : (
              <IconButton
                size="x-small"
                dataTest={`${node.value}_checkbox`}
                className="node-expand-collapse"
                iconName={expand ? 'remove' : 'add'}
                onClick={onExpand}
                tabIndex={virtualized ? -1 : undefined}
              />
            )}
          </div>
        )}
        <div data-test={node.value} className="node-value">
          <StyledCheckbox
            checked={isChecked || isPartiallyChecked}
            dashStyle={isPartiallyChecked}
            onChange={onCheck}
            $virtualized={virtualized}
            tabIndex={virtualized ? -1 : undefined}
          >
            <Typography variant="body1" component="span" className={`${isChecked ? 'checked' : 'unChecked'}`}>
              <Embolden term={searchTerm}>{node.label as string}</Embolden>
            </Typography>
          </StyledCheckbox>
        </div>
      </div>
      {children}
    </div>
  );
};

const StyledCheckbox = styled(Checkbox)<{ $virtualized?: boolean }>`
  ${({ $virtualized }) => ($virtualized ? $virtualizedStyle : undefined)}
  flex-grow: 1;
  span.label {
    flex-grow: 1;
  }
`;

const $virtualizedStyle = css`
  && {
    margin-right: 0;
    display: flex;
  }
`;
